import { useState } from 'react'
import { TrendingUp, Award, User, ChevronDown } from 'lucide-react'
import { useModuleDemoEnabled } from '@/hooks/useDemoDataAccess'
import { BIDashboardLive } from './biDashboardLive'
import {
  BarChart,
  Bar,
  Line,
  ComposedChart,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts'
import {
  CHART_LEGEND_STYLE,
  CHART_TOOLTIP_PROPS,
  chartAxisProps,
  chartGridProps,
  chartConversionRateColor,
  chartFunnelBarGradient,
  chartSeriesColor,
  chartSlaBarColor,
} from '@/config/chartTheme'

// ── Row 1: Monthly Origination Volume + Cumulative ───────────────────────
const originationData = [
  { month: 'Apr 25', volume: 142, cumulative: 142 },
  { month: 'May 25', volume: 168, cumulative: 310 },
  { month: 'Jun 25', volume: 124, cumulative: 434 },
  { month: 'Jul 25', volume: 195, cumulative: 629 },
  { month: 'Aug 25', volume: 156, cumulative: 785 },
  { month: 'Sep 25', volume: 210, cumulative: 995 },
  { month: 'Oct 25', volume: 178, cumulative: 1173 },
  { month: 'Nov 25', volume: 145, cumulative: 1318 },
  { month: 'Dec 25', volume: 230, cumulative: 1548 },
  { month: 'Jan 26', volume: 188, cumulative: 1736 },
  { month: 'Feb 26', volume: 215, cumulative: 1951 },
  { month: 'Mar 26', volume: 198, cumulative: 2149 },
]

// ── Row 2.1: Conversion Rates by Stage ───────────────────────────────────
const conversionData = [
  { stage: 'Received -> Quoted', rate: 72 },
  { stage: 'Quoted -> App Issued', rate: 58 },
  { stage: 'App Issued -> App Back', rate: 81 },
  { stage: 'App Back -> Commitment', rate: 64 },
  { stage: 'Commitment -> Rate Lock', rate: 88 },
  { stage: 'Rate Lock -> Funded', rate: 94 },
]

// ── Row 2.2: Time-to-Close by Product ────────────────────────────────────
const timeToCloseData = [
  { product: 'Senior Mtg', days: 67 },
  { product: 'Large Loan', days: 82 },
  { product: 'B-Note', days: 45 },
  { product: 'Mezz', days: 52 },
  { product: 'Ground Lease', days: 95 },
]

// ── Row 2.3: Originator Scorecard ────────────────────────────────────────
const originators = [
  { name: 'Sarah Chen', volume: 412, deals: 14, avgSize: 29.4, winRate: 68, avgDays: 54, revenue: 8.24, top: true },
  { name: 'David Reyes', volume: 388, deals: 11, avgSize: 35.3, winRate: 62, avgDays: 61, revenue: 7.76, top: false },
  { name: 'Marcus Webb', volume: 324, deals: 12, avgSize: 27.0, winRate: 58, avgDays: 58, revenue: 6.48, top: false },
  { name: 'Lisa Nakamura', volume: 298, deals: 9, avgSize: 33.1, winRate: 72, avgDays: 49, revenue: 5.96, top: false },
  { name: 'James Patel', volume: 276, deals: 10, avgSize: 27.6, winRate: 55, avgDays: 65, revenue: 5.52, top: false },
  { name: 'Angela Torres', volume: 251, deals: 8, avgSize: 31.4, winRate: 60, avgDays: 57, revenue: 5.02, top: false },
]

// ── Row 2.4: Revenue Attribution (Stacked Area) ─────────────────────────
const revenueData = [
  { month: 'Apr 25', origination: 2.8, servicing: 1.4, interest: 3.2 },
  { month: 'May 25', origination: 3.4, servicing: 1.5, interest: 3.3 },
  { month: 'Jun 25', origination: 2.5, servicing: 1.5, interest: 3.4 },
  { month: 'Jul 25', origination: 3.9, servicing: 1.6, interest: 3.5 },
  { month: 'Aug 25', origination: 3.1, servicing: 1.6, interest: 3.6 },
  { month: 'Sep 25', origination: 4.2, servicing: 1.7, interest: 3.7 },
  { month: 'Oct 25', origination: 3.6, servicing: 1.7, interest: 3.8 },
  { month: 'Nov 25', origination: 2.9, servicing: 1.8, interest: 3.9 },
  { month: 'Dec 25', origination: 4.6, servicing: 1.8, interest: 4.0 },
  { month: 'Jan 26', origination: 3.8, servicing: 1.9, interest: 4.1 },
  { month: 'Feb 26', origination: 4.3, servicing: 1.9, interest: 4.2 },
  { month: 'Mar 26', origination: 4.0, servicing: 2.0, interest: 4.3 },
]

// ── Row 3 Left: Client Acquisition Funnel ────────────────────────────────
const funnelStages = [
  { stage: 'Total Inquiries', count: 842, rate: null },
  { stage: 'Qualified', count: 486, rate: 57.7 },
  { stage: 'Quoted', count: 312, rate: 64.2 },
  { stage: 'Won', count: 148, rate: 47.4 },
  { stage: 'Funded', count: 118, rate: 79.7 },
]

// ── Row 3 Right: Market Penetration by MSA ───────────────────────────────
const marketData = [
  { msa: 'New York', deals: 28 },
  { msa: 'Los Angeles', deals: 22 },
  { msa: 'Dallas', deals: 19 },
  { msa: 'Miami', deals: 17 },
  { msa: 'Chicago', deals: 15 },
  { msa: 'Phoenix', deals: 13 },
  { msa: 'Atlanta', deals: 11 },
  { msa: 'Seattle', deals: 9 },
  { msa: 'Boston', deals: 8 },
  { msa: 'Denver', deals: 6 },
]

// ── User Profile Data (Item 16) ──────────────────────────────────────────
const USER_PROFILES = [
  {
    name: 'Sarah Chen',
    role: 'Senior Originator',
    volume: 412,
    deals: 14,
    winRate: 68,
    avgDays: 54,
    avgSize: 29.4,
    revenue: 8.24,
    stageConversions: [
      { stage: 'Received → Quoted', rate: 82 },
      { stage: 'Quoted → App', rate: 74 },
      { stage: 'App → Committee', rate: 69 },
      { stage: 'Committee → Funded', rate: 91 },
    ],
    topMarkets: ['Miami', 'Dallas', 'NYC'],
    topProductTypes: ['Senior Mortgage', 'Large Loan', 'B-Note'],
    dealsThisMonth: 3,
    volumeThisMonth: 87.5,
    openPipeline: 6,
  },
  {
    name: 'David Reyes',
    role: 'Originator',
    volume: 388,
    deals: 11,
    winRate: 62,
    avgDays: 61,
    avgSize: 35.3,
    revenue: 7.76,
    stageConversions: [
      { stage: 'Received → Quoted', rate: 75 },
      { stage: 'Quoted → App', rate: 68 },
      { stage: 'App → Committee', rate: 62 },
      { stage: 'Committee → Funded', rate: 88 },
    ],
    topMarkets: ['Chicago', 'Phoenix', 'Seattle'],
    topProductTypes: ['Large Loan', 'Senior Mortgage'],
    dealsThisMonth: 2,
    volumeThisMonth: 68.0,
    openPipeline: 5,
  },
  {
    name: 'Marcus Webb',
    role: 'Originator',
    volume: 324,
    deals: 12,
    winRate: 58,
    avgDays: 58,
    avgSize: 27.0,
    revenue: 6.48,
    stageConversions: [
      { stage: 'Received → Quoted', rate: 70 },
      { stage: 'Quoted → App', rate: 65 },
      { stage: 'App → Committee', rate: 58 },
      { stage: 'Committee → Funded', rate: 84 },
    ],
    topMarkets: ['Atlanta', 'Denver', 'Boston'],
    topProductTypes: ['B-Note', 'Mezz', 'Senior Mortgage'],
    dealsThisMonth: 2,
    volumeThisMonth: 52.0,
    openPipeline: 4,
  },
]

/**
 * Every array above is invented: six named originators ("Sarah Chen $412M,
 * $8.24M revenue"), $2.149B of monthly volume, an 842-inquiry funnel and a
 * revenue split — and it rendered on live accounts with no sample label, while
 * the register says member revenue is intentionally null (R10) and scorecard
 * definitions are undefined (R11) (CRE-21, EXEC-03, MOD-06). Only a demo
 * account sees it now. The live partition reads the deal analytics API for what
 * the book can answer (funded volume by month, stage conversion) and says which
 * definitions and data the rest is waiting for (biDashboardLive.tsx).
 */
export default function BIDashboard() {
  const demoEnabled = useModuleDemoEnabled('bi-dashboard')
  return demoEnabled ? <BIDashboardSample /> : <BIDashboardLive />
}

function BIDashboardSample() {
  const [selectedUser, setSelectedUser] = useState(USER_PROFILES[0].name)
  const userProfile = USER_PROFILES.find(u => u.name === selectedUser) ?? USER_PROFILES[0]

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-amber-400/15 bg-amber-400/[0.05] px-3 py-1.5 text-[11px] text-amber-200/80">
        Sample data — illustrative figures for demonstration, not a real book.
      </div>
      {/* Row 1: Origination Volume (full width) */}
      <section className="card px-4 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="terminal-kicker">Monthly Origination Volume</div>
            <p className="mt-1 text-[11px] text-stone-500">Bars = monthly volume ($M) | Line = cumulative volume ($M)</p>
          </div>
          <div className="flex items-center gap-4 text-[10px] text-stone-500">
            <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: chartSeriesColor(0) }} /> Monthly</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-4 rounded" style={{ background: chartSeriesColor(1) }} /> Cumulative</span>
          </div>
        </div>
        <div className="mt-3 h-56">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={originationData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid {...chartGridProps()} />
              <XAxis dataKey="month" {...chartAxisProps()} />
              <YAxis yAxisId="left" {...chartAxisProps()} tickFormatter={(v) => `$${v}M`} />
              <YAxis yAxisId="right" orientation="right" {...chartAxisProps()} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}B`} />
              <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(value: number, name: string) => [`$${value}M`, name === 'volume' ? 'Monthly' : 'Cumulative']} />
              <Bar yAxisId="left" dataKey="volume" fill={chartSeriesColor(0)} radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="cumulative" stroke={chartSeriesColor(1)} strokeWidth={2.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Row 2: Four charts (2x2) */}
      <section className="grid gap-3 xl:grid-cols-2">
        {/* 2.1 Conversion Rates by Stage */}
        <div className="card px-4 py-4">
          <div className="terminal-kicker">Conversion Rates by Stage</div>
          <p className="mt-1 text-[11px] text-stone-500">Transition conversion rate at each pipeline stage</p>
          <div className="mt-3 h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={conversionData} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                <CartesianGrid {...chartGridProps(false)} />
                <XAxis type="number" domain={[0, 100]} {...chartAxisProps()} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="stage" {...chartAxisProps(9)} width={130} />
                <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(value: number) => [`${value}%`, 'Conversion']} />
                <Bar dataKey="rate" radius={[0, 4, 4, 0]} barSize={16}>
                  {conversionData.map((_entry, i) => (
                    <Cell key={i} fill={chartSeriesColor(i)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 2.2 Time-to-Close by Product */}
        <div className="card px-4 py-4">
          <div className="terminal-kicker">Time-to-Close by Product</div>
          <p className="mt-1 text-[11px] text-stone-500">Average days from application to funding by product type</p>
          <div className="mt-3 h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timeToCloseData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid {...chartGridProps()} />
                <XAxis dataKey="product" {...chartAxisProps()} />
                <YAxis {...chartAxisProps()} tickFormatter={(v) => `${v}d`} />
                <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(value: number) => [`${value} days`, 'Avg Time']} />
                <Bar dataKey="days" fill={chartSeriesColor(0)} radius={[4, 4, 0, 0]} barSize={36}>
                  {timeToCloseData.map((entry, i) => (
                    <Cell key={i} fill={chartSlaBarColor(entry.days, { warn: 60, bad: 80 })} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 2.3 Originator Scorecard */}
        <div className="card overflow-hidden">
          <div className="border-b border-amber-400/10 px-4 py-3">
            <div className="terminal-kicker">Originator Scorecard</div>
            <p className="mt-1 text-[11px] text-stone-500">Ranked by origination volume — top performer highlighted</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-amber-400/10">
                  {['Rank', 'Originator', 'Volume ($M)', 'Deals', 'Avg Size ($M)', 'Win Rate', 'Avg Days', 'Revenue ($M)'].map((col) => (
                    <th key={col} className="whitespace-nowrap px-3 py-2.5 text-left text-[10px] font-medium text-stone-600">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {originators.map((o, i) => (
                  <tr key={o.name} className={`border-b border-amber-400/6 ${o.top ? 'bg-amber-400/[0.06]' : 'hover:bg-white/[0.02]'}`}>
                    <td className="px-3 py-2.5">
                      {o.top ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-300"><Award size={12} /> 1</span>
                      ) : (
                        <span className="text-xs text-stone-500">{i + 1}</span>
                      )}
                    </td>
                    <td className={`px-3 py-2.5 text-xs font-medium ${o.top ? 'text-amber-200' : 'text-stone-200'}`}>{o.name}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-stone-200">${o.volume}M</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-stone-300">{o.deals}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-stone-300">${o.avgSize.toFixed(1)}M</td>
                    <td className={`px-3 py-2.5 font-mono text-xs ${o.winRate >= 65 ? 'text-emerald-400' : 'text-stone-300'}`}>{o.winRate}%</td>
                    <td className={`px-3 py-2.5 font-mono text-xs ${o.avgDays <= 55 ? 'text-emerald-400' : 'text-stone-300'}`}>{o.avgDays}d</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-stone-200">${o.revenue.toFixed(2)}M</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 2.4 Revenue Attribution */}
        <div className="card px-4 py-4">
          <div className="terminal-kicker">Revenue Attribution</div>
          <p className="mt-1 text-[11px] text-stone-500">Monthly revenue by source ($M) — stacked area</p>
          <div className="mt-3 h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid {...chartGridProps()} />
                <XAxis dataKey="month" {...chartAxisProps()} />
                <YAxis {...chartAxisProps()} tickFormatter={(v) => `$${v}M`} />
                <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(value: number, name: string) => {
                  const labels: Record<string, string> = { origination: 'Origination Fees', servicing: 'Servicing', interest: 'Interest Income' }
                  return [`$${value.toFixed(1)}M`, labels[name] || name]
                }} />
                <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                <Area type="monotone" dataKey="interest" stackId="1" stroke={chartSeriesColor(2)} fill={chartSeriesColor(2)} name="Interest Income" />
                <Area type="monotone" dataKey="servicing" stackId="1" stroke={chartSeriesColor(1)} fill={chartSeriesColor(1)} name="Servicing" />
                <Area type="monotone" dataKey="origination" stackId="1" stroke={chartSeriesColor(0)} fill={chartSeriesColor(0)} name="Origination Fees" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Row 2.5: User Profile KPIs (Item 16) */}
      <section className="card px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="terminal-kicker">User-Level Reporting</div>
            <p className="mt-0.5 text-[11px] text-stone-500">Profile-based KPIs and per-originator conversion metrics</p>
          </div>
          <div className="relative flex items-center gap-1.5">
            <User size={13} className="text-stone-500" />
            <select
              className="appearance-none bg-black/40 border border-amber-400/15 rounded-md py-1.5 pl-2.5 pr-7 text-[11px] text-stone-300 outline-none"
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
            >
              {USER_PROFILES.map(u => <option key={u.name} value={u.name}>{u.name}</option>)}
            </select>
            <ChevronDown size={10} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-stone-600" />
          </div>
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          {/* Left: KPIs */}
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Volume YTD', value: `$${userProfile.volume}M`, color: 'text-amber-300' },
                { label: 'Deals YTD', value: String(userProfile.deals), color: 'text-stone-200' },
                { label: 'Win Rate', value: `${userProfile.winRate}%`, color: userProfile.winRate >= 65 ? 'text-emerald-400' : 'text-amber-400' },
                { label: 'Avg Deal Size', value: `$${userProfile.avgSize.toFixed(1)}M`, color: 'text-stone-200' },
                { label: 'Avg Days to Fund', value: `${userProfile.avgDays}d`, color: userProfile.avgDays <= 55 ? 'text-emerald-400' : 'text-amber-400' },
                { label: 'Revenue YTD', value: `$${userProfile.revenue.toFixed(2)}M`, color: 'text-amber-300' },
              ].map(m => (
                <div key={m.label} className="rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2.5">
                  <div className="text-[9px] uppercase tracking-[0.14em] text-stone-600">{m.label}</div>
                  <div className={`mt-1 text-sm font-semibold ${m.color}`}>{m.value}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <div className="flex-1 rounded-lg border border-blue-400/15 bg-blue-400/[0.04] px-3 py-2">
                <div className="text-[9px] uppercase tracking-wider text-stone-600">This Month</div>
                <div className="mt-0.5 text-xs font-semibold text-blue-300">{userProfile.dealsThisMonth} deals · ${userProfile.volumeThisMonth.toFixed(1)}M</div>
              </div>
              <div className="flex-1 rounded-lg border border-amber-400/15 bg-amber-400/[0.04] px-3 py-2">
                <div className="text-[9px] uppercase tracking-wider text-stone-600">Open Pipeline</div>
                <div className="mt-0.5 text-xs font-semibold text-amber-300">{userProfile.openPipeline} active deals</div>
              </div>
            </div>
            <div className="rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2.5">
              <div className="text-[10px] text-stone-500 mb-1.5">Top Markets</div>
              <div className="flex flex-wrap gap-1">{userProfile.topMarkets.map(m => <span key={m} className="px-1.5 py-0.5 rounded text-[10px] border border-amber-400/15 bg-amber-400/[0.05] text-amber-300/80">{m}</span>)}</div>
              <div className="text-[10px] text-stone-500 mb-1.5 mt-2">Top Products</div>
              <div className="flex flex-wrap gap-1">{userProfile.topProductTypes.map(p => <span key={p} className="px-1.5 py-0.5 rounded text-[10px] border border-stone-700 text-stone-400">{p}</span>)}</div>
            </div>
          </div>
          {/* Right: Stage conversions */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-stone-500 mb-3">Stage Conversion Rates</div>
            <div className="space-y-3">
              {userProfile.stageConversions.map(s => (
                <div key={s.stage} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-300">{s.stage}</span>
                    <span className={`font-mono ${s.rate >= 75 ? 'text-emerald-400' : s.rate >= 60 ? 'text-amber-400' : 'text-red-400'}`}>{s.rate}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${s.rate}%`,
                        background: chartConversionRateColor(s.rate),
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-lg border border-amber-400/10 bg-amber-400/[0.03] px-3 py-2.5">
              <div className="text-[10px] text-stone-500">Overall Funnel (Received → Funded)</div>
              <div className="mt-1 text-sm font-semibold text-amber-300">
                {(userProfile.stageConversions.reduce((acc, s) => acc * s.rate / 100, 1) * 100).toFixed(1)}% end-to-end
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Row 3: Funnel + Market Penetration */}
      <section className="grid gap-3 xl:grid-cols-2">
        {/* Client Acquisition Funnel */}
        <div className="card px-4 py-4">
          <div className="terminal-kicker">Client Acquisition Funnel</div>
          <p className="mt-1 text-[11px] text-stone-500">Lead-to-funded conversion with stage counts and rates</p>
          <div className="mt-4 space-y-2">
            {funnelStages.map((s, stageIndex) => {
              const maxCount = funnelStages[0].count
              const widthPct = Math.max((s.count / maxCount) * 100, 12)
              return (
                <div key={s.stage}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-300">{s.stage}</span>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-stone-200">{s.count}</span>
                      {s.rate !== null && (
                        <span className="font-mono text-[10px]" style={{ color: chartSeriesColor(0) }}>{s.rate}%</span>
                      )}
                    </div>
                  </div>
                  <div className="chart-viz-track mt-1 h-6 rounded-md">
                    <div
                      className="chart-viz-bar flex h-full items-center rounded-md px-2"
                      style={{
                        width: `${widthPct}%`,
                        background: chartFunnelBarGradient(stageIndex, funnelStages.length),
                      }}
                    >
                      <span className="text-[10px] font-semibold text-stone-100">{s.count}</span>
                    </div>
                  </div>
                  {s.rate !== null && (
                    <div className="mt-0.5 flex items-center gap-1 text-[10px] text-stone-600">
                      <TrendingUp size={9} /> {s.rate}% from previous stage
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          <div className="mt-3 rounded-lg border border-amber-400/10 bg-amber-400/[0.03] px-3 py-2">
            <div className="text-[10px] uppercase tracking-[0.14em] text-stone-600">Overall Conversion</div>
            <div className="mt-1 text-sm font-semibold text-amber-300">14.0% Inquiry-to-Fund</div>
          </div>
        </div>

        {/* Market Penetration by MSA */}
        <div className="card px-4 py-4">
          <div className="terminal-kicker">Market Penetration</div>
          <p className="mt-1 text-[11px] text-stone-500">Deal flow by metropolitan statistical area (MSA)</p>
          <div className="mt-3 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={marketData} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                <CartesianGrid {...chartGridProps(false)} />
                <XAxis type="number" {...chartAxisProps()} />
                <YAxis type="category" dataKey="msa" {...chartAxisProps()} width={80} />
                <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(value: number) => [`${value} deals`, 'Deal Count']} />
                <Bar dataKey="deals" radius={[0, 4, 4, 0]} barSize={14}>
                  {marketData.map((_entry, i) => (
                    <Cell key={i} fill={chartSeriesColor(i < 3 ? 0 : 1)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </div>
  )
}
